import React from 'react';
import SectionHeader from './SectionHeader';
import useInView from '../hooks/useInView';

const STRENGTHS = [
  {
    id: 'range',
    label: { ja: '幅広さ', en: 'Range' },
    heading: {
      ja: '理系も文系も、営業も開発も',
      en: 'Science and humanities, sales and engineering',
    },
    body: {
      ja: [
        '理系と文系を行き来してきたので、課題に応じて視点を切り替えられます。',
        '営業経験があるので顧客対応に強く、開発側としても要件の解像度を落としません。',
      ],
      en: [
        'I can switch perspectives quickly because I have worked across both technical and business contexts.',
        'I communicate well with customers while still keeping engineering requirements precise.',
      ],
    },
    receipts: {
      ja: ['東大 経済学部 → BCIT CS', 'サイボウズで約150社の導入提案'],
      en: ['UTokyo Economics → BCIT CS', '~150 client proposals at Cybozu'],
    },
  },
  {
    id: 'learning',
    label: { ja: '学習能力', en: 'Learning' },
    heading: {
      ja: '限られた時間で、新しい領域をモノにする',
      en: 'Fast ramp-up in unfamiliar domains',
    },
    body: {
      ja: [
        '限られた時間でも要点を見極め、実務で使えるレベルまで引き上げます。',
        '馴染みのない領域でも、まず手を動かして検証し、短期間で戦力化します。',
      ],
      en: [
        'I identify the essentials quickly and turn them into practical execution.',
        'Even in new areas, I prototype early and become productive in a short time.',
      ],
    },
    receipts: {
      ja: ['応用情報技術者：学習40時間で高得点合格', 'kintone認定ADS：入社半年で取得'],
      en: [
        'Applied IT Engineer exam: high score with ~40 hours of study',
        'kintone App Design Specialist: within 6 months of joining',
      ],
    },
  },
  {
    id: 'execution',
    label: { ja: '行動力', en: 'Execution' },
    heading: {
      ja: '思い立ったら動き、決めたらやり切る',
      en: 'Move first, then see it through',
    },
    body: {
      ja: [
        '世界中への冒険旅やカナダ留学など、思い立ったら一歩目を踏み出します。',
        '一度決めたことは、厳しい環境でもやり切る胆力があります。',
      ],
      en: [
        'I take action decisively, from global travel challenges to major life decisions like studying abroad.',
        'Once committed, I keep pushing until the outcome is delivered.',
      ],
    },
    receipts: {
      ja: ['世界各地をまたぐ冒険の実行', '2年間のカナダ留学を完走'],
      en: ['Adventures across the world', 'Completed two years of study in Canada'],
    },
  },
];

function StrengthBlock({ strength, language, order }) {
  const [blockRef, isVisible] = useInView();

  return (
    <article
      ref={blockRef}
      className={`strength reveal ${isVisible ? 'is-visible' : ''}`}
      style={{ '--i': order }}
    >
      <p className="strength__number mono">
        {String(order + 1).padStart(2, '0')} — {strength.label[language]}
      </p>
      <h3 className="strength__heading">{strength.heading[language]}</h3>
      {strength.body[language].map((sentence, index) => (
        <p key={`${strength.id}-body-${index}`} className="strength__body">
          {sentence}
        </p>
      ))}
      <ul className="strength__receipts" aria-label={language === 'ja' ? '裏付け' : 'Receipts'}>
        {strength.receipts[language].map((receipt) => (
          <li key={receipt} className="mono">
            {receipt}
          </li>
        ))}
      </ul>
    </article>
  );
}

// The triangle diagram, looping: the three color-coded strengths pop in,
// spin faster and faster around the center, collapse into one, and fuse into
// a single "yoshidon" circle with a sparkle — then it all begins again.
// (Static triangle under prefers-reduced-motion.)
function StrengthTriangle({ language, isVisible }) {
  const labels =
    language === 'ja'
      ? ['幅広さ', '学習能力', '行動力']
      : ['Range', 'Learning', 'Execution'];

  const vertices = [
    { x: 110, y: 42 },
    { x: 36, y: 182 },
    { x: 184, y: 182 },
  ];

  // Centroid of the three vertices — the spin/fusion center.
  const cx = 110;
  const cy = 135.3;

  return (
    <svg
      className={`strengths-triangle ${isVisible ? 'is-visible' : ''}`}
      viewBox="-26 -6 272 286"
      role="img"
      aria-label={
        language === 'ja'
          ? '幅広さ・学習能力・行動力の3つが融合してyoshidonになる図'
          : 'Range, learning and execution fusing into one: yoshidon'
      }
      focusable="false"
    >
      <g className="strengths-triangle__spin">
        <path
          className="strengths-triangle__edges"
          d={`M${vertices[0].x} ${vertices[0].y} L${vertices[1].x} ${vertices[1].y} L${vertices[2].x} ${vertices[2].y} Z`}
        />
        {vertices.map((v, i) => (
          <g key={labels[i]} className={`strengths-triangle__node strengths-triangle__node--${i + 1}`}>
            <circle cx={v.x} cy={v.y} r="26" />
            <text x={v.x} y={v.y + 4} textAnchor="middle">
              {labels[i]}
            </text>
          </g>
        ))}
      </g>

      <g className="strengths-triangle__fusion">
        <g className="strengths-triangle__sparkles" aria-hidden="true">
          {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
            <line
              key={angle}
              x1="0"
              y1="-56"
              x2="0"
              y2="-74"
              style={{ '--spark-angle': `${angle}deg` }}
            />
          ))}
        </g>
        <circle className="strengths-triangle__fusion-circle" cx={cx} cy={cy} r="46" />
        <text className="strengths-triangle__fusion-name mono" x={cx} y={cy + 5} textAnchor="middle">
          yoshidon
        </text>
      </g>
    </svg>
  );
}

function StrengthsSection({ language }) {
  const [diagramRef, diagramVisible] = useInView({ threshold: 0.3 });

  const copy =
    language === 'ja'
      ? {
          label: 'STRENGTHS',
          title: '正直、ここが自信あります',
          lead: '幅広さ・学習能力・行動力。成果につながる3つの強みです。',
        }
      : {
          label: 'STRENGTHS',
          title: "Where I'm honestly confident",
          lead: 'Three strengths that repeatedly drive outcomes: range, learning velocity, and execution.',
        };

  return (
    <section id="strengths" className="strengths-section" aria-label={copy.title}>
      <SectionHeader index="04" label={copy.label} title={copy.title} lead={copy.lead} />

      <div className="strengths-layout">
        <div ref={diagramRef} className="strengths-figure">
          <StrengthTriangle language={language} isVisible={diagramVisible} />
        </div>

        <div className="strengths-grid">
          {STRENGTHS.map((strength, index) => (
            <StrengthBlock key={strength.id} strength={strength} language={language} order={index} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default StrengthsSection;
